import React, { useEffect, useMemo, useState } from 'react';
import { ReservationListPageStyled } from './reservation-list.page.styled';
import Dashboard from '../../../components/shared/dashboard/dashboard';
import List from '../../../components/shared/list/list';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteReservationAction,
  getReservationListAction,
  getReservationListResetState,
  getTBLinkAction,
  reservationListPartialResetAction,
} from '../../../store/shared/reservations-list.store';
import {
  ListStyled,
  sxList,
} from '../../../components/shared/list/list.styled';
import { SvgImage, SvgType } from '../../../components/base/svg/svg';
import { Colors } from '../../../colors';
import {
  getDateTimeBooking,
  getDisplayNameStatusBooking,
  getRoom,
  getSmartObject,
  getStringDateName,
} from '../../../utils/utils';
import i18n from '../../../translations/strings';
import { useModal } from '../../../components/shared/modal-provider/hooks/modal-provider.hook';
import { ModalVariant } from '../../../components/shared/modal-provider/components/modal/modal.constants';
import {
  sendEmailAction,
  sendEmailResetState,
} from '../../../store/shared/send-email.store';
import SendEmailModal from '../../../components/shared/send-email-modal/send-email-modal';
import TableCell from '@mui/material/TableCell';
import CsvUploadModal from './components/csv-upload-modal';
import { useNavigate } from 'react-router-dom';
import {
  uploadCsvAction,
  uploadCsvResetState,
} from '../../../store/customers/upload-csv.store';
import moment from 'moment/moment';
import CustomChip from '../../../components/shared/custom-chip/custom-chip';
import { CsvUploadModalVariant } from './components/constants';

const ReservationListPage = () => {
  const { openModal } = useModal();
  const dispatch = useDispatch();

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [selectedEventCsv, setSelectedEventCsv] = useState<any>(null);
  const [csvModalVariant, setCsvModalVariant] = useState<CsvUploadModalVariant>(
    CsvUploadModalVariant.Add,
  );

  const [showCsvModal, setShowCsvModal] = useState(false);
  const [titleListAttendees, setTitleListAttendees] = useState(
    i18n.t('reservations.attendeesList'),
  );

  const statuses = ['CONFIRMED', 'SENT'];

  const {
    reservationsList,
    paginationInfo,
    loading,
    error,
    rooms,
    smartObjects,
    updateDeleteSuccess,
    tbLink,
    errorTBLink,
  } = useSelector((state: any) => {
    return state.reservationsList;
  });

  const { loadingEmail, errorEmail, sendEmailSuccess } = useSelector(
    (state: any) => {
      return state.sendEmail;
    },
  );

  const { loadingCsv, errorCsv, uploadCsvSuccess } = useSelector(
    (state: any) => {
      return state.uploadCSV;
    },
  );

  const onEmailClick = (reservation: any) => {
    setShowEmailModal(true);
    setSelectedReservation(reservation);
  };

  const onCsvClick = (item: any, csvModalVariant: CsvUploadModalVariant) => {
    setSelectedEventCsv(item.id);
    setCsvModalVariant(csvModalVariant);
    setShowCsvModal(true);
  };

  const onSendEmail = (message: string) => {
    dispatch(
      sendEmailAction({
        text: message,
        booking_id: selectedReservation.id,
      }),
    );
    setShowEmailModal(false);
  };

  const handleCancelReservation = (message: string, reservation: any) => {
    dispatch(
      deleteReservationAction({
        id: reservation.id,
        message: message,
      }),
    );
  };

  const getTBLink = (id: string) => {
    dispatch(getTBLinkAction(id));
  };

  const navigate = useNavigate();

  const fields = useMemo(() => {
    return [
      {
        name: 'created_at',
        displayName: i18n.t('reservations.booking'),
        component: (item: any) => {
          const date = getStringDateName(item.created_at);
          return <ListStyled.CustomRow>{date}</ListStyled.CustomRow>;
        },
      },
      {
        name: 'id_reservation',
        displayName: i18n.t('reservations.reservationId'),
        component: (item: any) => {
          return (
            <ListStyled.CustomRow>
              {item.reference_number ? item.reference_number : item.id}
            </ListStyled.CustomRow>
          );
        },
      },
      {
        name: 'n_events',
        displayName: i18n.t('reservations.nOfEvents'),
        component: (item: any) => {
          return (
            <ListStyled.CustomRow>{item.interval.length}</ListStyled.CustomRow>
          );
        },
      },
      {
        name: 'status',
        displayName: i18n.t('reservations.status'),
        component: (item: any) => (
          <ListStyled.StatusRow
            fontFamily={
              item.status === 'CONFIRMED'
                ? 'EncodeSans-SemiBold'
                : 'EncodeSans-Regular'
            }
            color={
              item.status === 'CONFIRMED' ? Colors.MediumBlue : Colors.Black
            }
          >
            {getDisplayNameStatusBooking(item.status)}
          </ListStyled.StatusRow>
        ),
      },
      {
        name: 'edit',
        displayName: i18n.t('reservations.edit'),
        component: (item: any) => (
          <ListStyled.ContainerEdit>
            <ListStyled.ContainerEditMail onClick={() => onEmailClick(item)}>
              <SvgImage
                options={{ color: Colors.PrimaryDark }}
                type={SvgType.Mail}
              />
              <ListStyled.EditMessage>
                {i18n.t('reservations.sendMessage')}
              </ListStyled.EditMessage>
            </ListStyled.ContainerEditMail>
            {item.status !== 'CONFIRMED' &&
              item.status !== 'CANCELED_BY_USER' && (
                <ListStyled.ContainerEditTrash
                  onClick={() => {
                    openModal({
                      message: i18n.t(
                        'handleReservation.cancelReservationWarning',
                      ),
                      variant: ModalVariant.ConfirmationRequest,
                      onConfirmClick: (message: string) =>
                        handleCancelReservation(message, item),
                    });
                  }}
                >
                  <SvgImage
                    options={{ color: Colors.Unavailability }}
                    type={SvgType.Trash}
                  />
                  <ListStyled.EditDelete>
                    {i18n.t('reservations.delete')}
                  </ListStyled.EditDelete>
                </ListStyled.ContainerEditTrash>
              )}
          </ListStyled.ContainerEdit>
        ),
      },
    ];
  }, []);

  const fieldsExpandedRows = useMemo(() => {
    return [
      {
        name: 'date',
        displayName: i18n.t('reservations.date'),
        component: (item: any) => {
          const date = getStringDateName(item.start);
          return (
            <TableCell
              key={item.id_reservation}
              sx={sxList.expandedRow}
              align="left"
            >
              <ListStyled.ExpandedRow>{date}</ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'time',
        displayName: i18n.t('reservations.time'),
        component: (item: any) => {
          const { time: timeStart } = getDateTimeBooking(item.start);
          const { time: timeEnd } = getDateTimeBooking(item.end);
          return (
            <TableCell
              key={item.id_reservation}
              sx={sxList.expandedRow}
              align="left"
            >
              <ListStyled.ExpandedRow>
                {i18n.t('reservations.timeStartEnd', {
                  start: timeStart,
                  end: timeEnd,
                })}
              </ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'room',
        displayName: i18n.t('reservations.room'),
        component: (item: any) => {
          const room = getRoom(rooms, item.product);
          return (
            <TableCell
              key={item.id_reservation}
              sx={sxList.expandedRow}
              align="left"
            >
              <ListStyled.ExpandedRow>{room.name}</ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'in_presence',
        displayName: i18n.t('reservations.inPresence'),
        component: (item: any) => {
          return (
            <TableCell
              key={item.id_reservation}
              sx={sxList.expandedRow}
              align="left"
            >
              <ListStyled.ExpandedRow>
                {item.attendees
                  ? `${item.attendees} invitat${item.attendees > 1 ? 'i' : 'o'}`
                  : '-'}
              </ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'online',
        displayName: i18n.t('reservations.online'),
        component: (item: any) => {
          console.log(item)
          return (
            <TableCell
              key={item.id_reservation}
              sx={sxList.expandedRow}
              align="left"
            >
              <ListStyled.ExpandedRow>
                {item.remote_attendees
                  ? `${item.remote_attendees} invitat${
                      item.remote_attendees > 1 ? 'i' : 'o'
                    }`
                  : '-'}
              </ListStyled.ExpandedRow>
              {item.zoomevent && (
                <ListStyled.CustomZoomDetail
                  target="_blank"
                  to={item.zoomevent.link}
                >
                  <ListStyled.CustomZoomDetailTitle>
                    {i18n.t('reservations.logEventZoom')}
                  </ListStyled.CustomZoomDetailTitle>
                </ListStyled.CustomZoomDetail>
              )}
            </TableCell>
          );
        },
      },
      {
        name: 'services',
        displayName: i18n.t('reservations.services'),
        component: (item: any) => {
          const servicesRoom = getRoom(rooms, item.product).services;
          const selectedServices = servicesRoom.filter((serviceRoom: any) =>
            item.services.some((service: string) => serviceRoom.id === service),
          );
          const showSelectedSmartObject = selectedServices.some(
            (service: any) => service.operator_choice_required === true,
          );
          const selectedSmartObject = showSelectedSmartObject
            ? ` | ${i18n.t('reservations.tools')}: ` +
              getSmartObject(smartObjects, item.operator_management_choice)
            : '';

          return (
            <TableCell
              key={item.id_reservation}
              sx={sxList.servicesRow}
              align="left"
            >
              <ListStyled.ExpandedRow>
                {selectedServices.length > 0
                  ? selectedServices
                      .map((service: any) => service.name)
                      .join(' | ') + selectedSmartObject
                  : '-'}
              </ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'list_attendees',
        displayName: titleListAttendees,
        component: (item: any) => {
          if (item.attendees <= 0 || item.check_out) {
            setTitleListAttendees('');
          }
          return (
            <TableCell
              key={item.id_reservation}
              sx={sxList.expandedRow}
              align="left"
            >
              {
                !item.check_out &&
                <ListStyled.ContainerAttendeeListCell>
                  {item?.has_attendees_list && !item.check_in && (
                    <>
                      <ListStyled.ContainerAttendeeList
                        onClick={() =>
                          onCsvClick(item, CsvUploadModalVariant.Edit)
                        }
                      >
                        <SvgImage type={SvgType.EditCsv} />
                        <ListStyled.ContainerListAttendeesDescription>
                          {i18n.t('reservations.editCsv')}
                        </ListStyled.ContainerListAttendeesDescription>
                      </ListStyled.ContainerAttendeeList>
                      <ListStyled.ContainerAttendeeList
                        onClick={() => {
                          navigate(`/customer/list-attendees/${item.id}`);
                        }}
                      >
                        <SvgImage type={SvgType.Eye} />
                        <ListStyled.ContainerListAttendeesDescription>
                          {i18n.t('reservations.view')}
                        </ListStyled.ContainerListAttendeesDescription>
                      </ListStyled.ContainerAttendeeList>
                    </>
                  )}
                  {item?.has_attendees_list && item.check_in && (
                    <ListStyled.ContainerAttendeeList
                      onClick={() => {
                        navigate(`/customer/list-attendees/${item.id}`);
                      }}
                    >
                      <SvgImage type={SvgType.HandleAttendees} />
                      <ListStyled.ContainerListAttendeesDescription>
                        {i18n.t('reservations.handleAttendees')}
                      </ListStyled.ContainerListAttendeesDescription>
                    </ListStyled.ContainerAttendeeList>
                  )}
                  {!item?.has_attendees_list && !item.check_in && (
                    <ListStyled.ContainerAttendeeList
                      onClick={() =>
                        onCsvClick(item, CsvUploadModalVariant.Add)
                      }
                    >
                      <SvgImage type={SvgType.Add} />
                      <ListStyled.ContainerListAttendeesDescription>
                        {i18n.t('reservations.add')}
                      </ListStyled.ContainerListAttendeesDescription>
                    </ListStyled.ContainerAttendeeList>
                  )}
                  {!item?.has_attendees_list && item.check_in && (
                    <ListStyled.ContainerNoListUploaded>
                      <ListStyled.ContainerNoListDescription>
                        {i18n.t('reservations.noListUploaded')}
                      </ListStyled.ContainerNoListDescription>
                    </ListStyled.ContainerNoListUploaded>
                  )}
                </ListStyled.ContainerAttendeeListCell>
              }
            </TableCell>
          );
        },
      },
      {
        name: 'thingsboard',
        displayName: '',
        component: (item: any) => {
          const isEventActive =
            (item.is_operator_management_choice_self_management &&
              item.check_in && !item.check_out)

          return (
            <TableCell
              key={item.id_reservation}
              sx={sxList.servicesRow}
              align="left"
            >
              {isEventActive && (
                <CustomChip
                  onClick={() => getTBLink(item.id)}
                  label={i18n.t('reservations.loginToThingsboard')}
                />
              )}
            </TableCell>
          );
        },
      },
    ];
  }, [reservationsList, rooms, titleListAttendees]);

  useEffect(() => {
    if (error) {
      openModal({
        message: error,
        variant: ModalVariant.Error,
        handleCloseModal: () => dispatch(getReservationListResetState()),
      });
    }

    if (errorEmail) {
      openModal({
        message: errorEmail,
        variant: ModalVariant.Error,
        handleCloseModal: () => dispatch(sendEmailResetState()),
      });
    }
  }, [error, errorEmail]);

  useEffect(() => {
    if (sendEmailSuccess) {
      openModal({
        message: i18n.t('sendEmail.emailSuccessMessage'),
        variant: ModalVariant.Success,
        handleCloseModal: () => dispatch(sendEmailResetState()),
      });
    }
  }, [sendEmailSuccess]);

  useEffect(() => {
    if (uploadCsvSuccess) {
      openModal({
        message: i18n.t('reservations.csvSuccessUpload'),
        variant: ModalVariant.Success,
      });
    }
    dispatch(uploadCsvResetState());
  }, [uploadCsvSuccess]);

  useEffect(() => {
    if (tbLink) {
      window.open(tbLink, '_blank');
    }
    dispatch(reservationListPartialResetAction());
  }, [tbLink]);

  useEffect(() => {
    if (errorTBLink) {
      openModal({
        message: errorTBLink,
        variant: ModalVariant.Error,
        handleCloseModal: () => dispatch(reservationListPartialResetAction()),
      });
    }
  }, [errorTBLink]);

  useEffect(() => {
    if (errorCsv) {
      openModal({
        message: errorCsv,
        variant: ModalVariant.Error,
      });
    }
    dispatch(uploadCsvResetState());
  }, [errorCsv]);

  const handleCsvModalClose = () => {
    setShowCsvModal(false);
    setSelectedEventCsv(null);
  };
  const uploadCsvFile = (file: File) => {
    dispatch(uploadCsvAction({ file_csv: file, interval: selectedEventCsv }));
  };

  useEffect(() => {
    if (updateDeleteSuccess) {
      openModal({
        message: i18n.t('reservations.deleteReservationSuccess'),
        variant: ModalVariant.Success,
      });
      dispatch(
        getReservationListAction({
          page: paginationInfo.pageCount,
          pageSize: paginationInfo.pageSize,
          statuses: statuses,
        }),
      );
    }
  }, [updateDeleteSuccess]);

  return (
    <Dashboard callingPage={'ReservationListPage'}>
      <ReservationListPageStyled.Container>
        <ReservationListPageStyled.TitlePage>
          {i18n.t('reservations.myReservations')}
        </ReservationListPageStyled.TitlePage>
        <ReservationListPageStyled.SubtitlePage>
          {i18n.t('reservations.subtitleReservationPage')}
        </ReservationListPageStyled.SubtitlePage>
        <ReservationListPageStyled.ListContainer>
          <List
            containerComponent={ReservationListPageStyled.ListContainer}
            data={reservationsList}
            paginationInfo={paginationInfo}
            fields={fields}
            getDataAction={getReservationListAction}
            expandedRows={{
              fields: fieldsExpandedRows,
              selectedKeyRow: 'interval',
            }}
            refreshList={uploadCsvSuccess}
            loading={loading || loadingEmail || loadingCsv}
            statuses={statuses}
            showActiveEvents
          />
        </ReservationListPageStyled.ListContainer>
        <SendEmailModal
          isVisible={showEmailModal}
          onModalClose={() => {
            setShowEmailModal(false);
            setSelectedReservation(null);
          }}
          selectedReservation={selectedReservation}
          onSendEmail={(message: string) => onSendEmail(message)}
        />

        <CsvUploadModal
          variant={csvModalVariant}
          isVisible={showCsvModal}
          onModalClose={handleCsvModalClose}
          onCsvUpload={(file: any) => uploadCsvFile(file)}
        />
      </ReservationListPageStyled.Container>
    </Dashboard>
  );
};

export default ReservationListPage;
